import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { 
  Mail, 
  Lock, 
  Zap, 
  Eye, 
  EyeOff, 
  Building2,
  QrCode,
  ArrowLeftRight,
  CreditCard,
  FileText,
  Users,
  Shield,
  TrendingUp,
  Wallet,
  CheckCircle2,
  Key,
  Loader2
} from 'lucide-react';
import { Button, Input, Card, CardBody } from '../components/ui';
import { useAuthStore } from '../stores/auth.store';
import { api } from '../lib/api';

// Fixed tenant configuration (from env or fallback)
const FIXED_TENANT_ID = import.meta.env.VITE_FIXED_TENANT_ID || '';
const FIXED_TENANT_NAME = import.meta.env.VITE_FIXED_TENANT_NAME || '';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAACyJD3wxQ9WjYJAJ';

const loginSchema = z.object({
  email: z.string().email({ message: 'Email inválido' }),
  password: z.string().min(6, 'Senha deve ter no mínimo 6 caracteres'),
  tenantId: z.string().min(1, 'Tenant ID é obrigatório'),
});

type LoginForm = z.infer<typeof loginSchema>;

const features = [
  {
    icon: QrCode,
    title: 'Pix Instantâneo',
    description: 'Receba e envie Pix em tempo real com QR Codes dinâmicos',
  },
  {
    icon: ArrowLeftRight,
    title: 'Transferências',
    description: 'Transfira entre contas internas sem taxas',
  },
  {
    icon: CreditCard,
    title: 'Cartão de Crédito',
    description: 'Aceite pagamentos com cartão em até 12x',
  },
  {
    icon: FileText,
    title: 'Boletos',
    description: 'Gere boletos registrados automaticamente',
  },
  {
    icon: Users,
    title: 'Multi-usuários',
    description: 'Gerencie funcionários, clientes e fornecedores',
  },
  {
    icon: TrendingUp,
    title: 'Split de Pagamentos',
    description: 'Divida recebimentos automaticamente',
  },
];

const stats = [
  { value: '99.9%', label: 'Uptime' },
  { value: '<1s', label: 'Tempo Pix' },
  { value: '256-bit', label: 'Criptografia' },
];

export function Login() {
  const navigate = useNavigate();
  const { login, isLoading, error, clearError } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [twoFAError, setTwoFAError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      tenantId: FIXED_TENANT_ID || '',
    },
  });

  const onSubmit = async (data: LoginForm) => {
    try {
      const result = await login(data.email, data.password, data.tenantId, turnstileToken || undefined);
      
      // Check if 2FA is required
      if (result?.requires2FA && result.tempToken) {
        setRequires2FA(true);
        setTempToken(result.tempToken);
        return;
      }
      
      navigate('/');
    } catch {
      // Error is handled by store — reset turnstile for next attempt
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  const handleVerify2FA = async () => {
    if (totpCode.length !== 6) return;
    
    setVerifying2FA(true);
    setTwoFAError('');
    
    try {
      const response = await api.post('/auth/verify-2fa', {
        tempToken,
        code: totpCode,
      });
      
      // Complete login with the verified token
      const { accessToken, refreshToken, user } = response.data;
      useAuthStore.getState().setAuth(accessToken, refreshToken, user);
      navigate('/');
    } catch (err: any) {
      setTwoFAError(err.response?.data?.message || 'Código inválido. Tente novamente.');
      setTotpCode('');
    } finally {
      setVerifying2FA(false);
    }
  };

  const handleUseBackupCode = async () => {
    if (totpCode.length < 8) return;
    
    setVerifying2FA(true);
    setTwoFAError('');
    
    try {
      const response = await api.post('/auth/verify-backup-code', {
        tempToken,
        backupCode: totpCode,
      });
      
      const { accessToken, refreshToken, user } = response.data;
      useAuthStore.getState().setAuth(accessToken, refreshToken, user);
      navigate('/');
    } catch (err: any) {
      setTwoFAError(err.response?.data?.message || 'Código de backup inválido.');
      setTotpCode('');
    } finally {
      setVerifying2FA(false);
    }
  };

  return (
    <div className="min-h-screen flex relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-accent-500/10 rounded-full blur-3xl" />
        <div className="grid-bg absolute inset-0 opacity-10" />
      </div>

      {/* Left Side - Features */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 p-8 xl:p-16 flex-col justify-center relative">
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          {/* Logo & Title */}
          <div className="flex items-center gap-4 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shadow-glow">
              <Zap className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold gradient-text">{import.meta.env.VITE_APP_NAME || 'Hub Business'}</h1>
              <p className="text-surface-400">Plataforma Financeira Inteligente</p>
            </div>
          </div>

          {/* Main Headline */}
          <h2 className="text-4xl xl:text-5xl font-bold text-surface-100 mb-6 leading-tight">
            Gerencie suas finanças com{' '}
            <span className="gradient-text">tecnologia de ponta</span>
          </h2>

          <p className="text-lg text-surface-400 mb-10">
            Uma plataforma completa para empresas que precisam de agilidade, 
            segurança e controle total sobre suas operações financeiras.
          </p>

          {/* Features Grid */}
          <div className="grid grid-cols-2 gap-4 mb-10">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 * index }}
                className="flex items-start gap-3 p-4 rounded-xl bg-surface-900/50 border border-surface-800/50 hover:border-primary-500/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 text-primary-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-surface-200 text-sm">{feature.title}</h3>
                  <p className="text-xs text-surface-500 mt-0.5">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.5 + 0.1 * index }}
                className="text-center"
              >
                <div className="text-2xl font-bold gradient-text">{stat.value}</div>
                <div className="text-xs text-surface-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>

          {/* Trust Badges */}
          <div className="flex items-center gap-6 mt-10 pt-10 border-t border-surface-800/50">
            <div className="flex items-center gap-2 text-surface-500 text-sm">
              <Shield className="w-4 h-4 text-green-500" />
              <span>PCI DSS Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-surface-500 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>LGPD Compliant</span>
            </div>
            <div className="flex items-center gap-2 text-surface-500 text-sm">
              <Wallet className="w-4 h-4 text-green-500" />
              <span>Banco Central</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 mb-4 shadow-glow">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-3xl font-bold gradient-text">{import.meta.env.VITE_APP_NAME || 'Hub Business'}</h1>
            <p className="text-surface-400 mt-2">Plataforma Financeira Inteligente</p>
          </div>

          {/* Login Card */}
          <Card variant="glass" className="backdrop-blur-xl">
            <CardBody className="p-8">
              {!requires2FA ? (
                <>
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-surface-100">
                      Bem-vindo de volta
                    </h2>
                    <p className="text-surface-400 mt-1">
                      Entre com suas credenciais para acessar
                    </p>
                  </div>

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm"
                    >
                      {error}
                      <button
                        onClick={clearError}
                        className="float-right text-red-300 hover:text-red-200"
                      >
                        ×
                      </button>
                    </motion.div>
                  )}

                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                    {FIXED_TENANT_ID ? (
                      <div className="p-3 bg-primary-500/10 border border-primary-500/20 rounded-lg flex items-center gap-3">
                        <Building2 className="w-5 h-5 text-primary-400" />
                        <div>
                          <p className="text-xs text-surface-400">Empresa</p>
                          <p className="text-sm font-semibold text-surface-200">{FIXED_TENANT_NAME || FIXED_TENANT_ID}</p>
                        </div>
                        <input type="hidden" {...register('tenantId')} />
                      </div>
                    ) : (
                      <Input
                        label="Empresa (Tenant ID)"
                        type="text"
                        placeholder="Digite o ID da empresa"
                        leftIcon={<Building2 className="w-4 h-4" />}
                        error={errors.tenantId?.message}
                        {...register('tenantId')}
                      />
                    )}

                    <Input
                      label="Email"
                      type="email"
                      placeholder="seu@email.com"
                      leftIcon={<Mail className="w-4 h-4" />}
                      error={errors.email?.message}
                      {...register('email')}
                    />

                    <Input
                      label="Senha"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      leftIcon={<Lock className="w-4 h-4" />}
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="text-surface-500 hover:text-surface-300"
                        >
                          {showPassword ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      }
                      error={errors.password?.message}
                      {...register('password')}
                    />

                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-surface-700 bg-surface-900 text-primary-500 focus:ring-primary-500"
                        />
                        <span className="text-sm text-surface-400">Lembrar-me</span>
                      </label>
                      <Link
                        to="/recovery"
                        className="text-sm text-primary-400 hover:text-primary-300"
                      >
                        Esqueceu a senha?
                      </Link>
                    </div>

                    <Button
                      type="submit"
                      variant="primary"
                      className="w-full"
                      isLoading={isLoading}
                      disabled={!turnstileToken}
                    >
                      Entrar
                    </Button>

                    {/* Cloudflare Turnstile */}
                    <div className="flex justify-center">
                      <Turnstile
                        ref={turnstileRef}
                        siteKey={TURNSTILE_SITE_KEY}
                        onSuccess={(token) => setTurnstileToken(token)}
                        onError={() => setTurnstileToken(null)}
                        onExpire={() => {
                          setTurnstileToken(null);
                          turnstileRef.current?.reset();
                        }}
                        options={{
                          theme: 'dark',
                          size: 'flexible',
                        }}
                      />
                    </div>
                  </form>

                  {/* Hide "Fale conosco" for fixed-tenant deploys (e.g. Invest Redenção) where users only enter via invite links */}
                  {!FIXED_TENANT_ID && (
                    <div className="mt-6 text-center">
                      <p className="text-surface-500 text-sm">
                        Não tem uma conta?{' '}
                        <a href="#" className="text-primary-400 hover:text-primary-300 font-medium">
                          Fale conosco
                        </a>
                      </p>
                    </div>
                  )}
                </>
              ) : (
                /* 2FA Verification Step */
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-6"
                >
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
                      <Key className="w-8 h-8 text-primary-400" />
                    </div>
                    <h2 className="text-xl font-bold text-surface-100">
                      Verificação em Duas Etapas
                    </h2>
                    <p className="text-surface-400 mt-2 text-sm">
                      Digite o código de 6 dígitos do seu aplicativo autenticador
                    </p>
                  </div>

                  {twoFAError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg text-red-400 text-sm"
                    >
                      {twoFAError}
                    </motion.div>
                  )}

                  <div className="space-y-4">
                    <Input
                      label="Código de Verificação"
                      type="text"
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      maxLength={6}
                      className="text-center text-2xl tracking-widest"
                      autoFocus
                    />

                    <Button
                      onClick={handleVerify2FA}
                      variant="primary"
                      className="w-full"
                      disabled={totpCode.length !== 6 || verifying2FA}
                    >
                      {verifying2FA ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verificando...
                        </>
                      ) : (
                        'Verificar'
                      )}
                    </Button>
                  </div>

                  <div className="border-t border-surface-800 pt-4">
                    <p className="text-xs text-surface-500 text-center mb-3">
                      Perdeu acesso ao autenticador?
                    </p>
                    <button
                      onClick={handleUseBackupCode}
                      className="w-full text-sm text-primary-400 hover:text-primary-300"
                    >
                      Usar código de backup
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setRequires2FA(false);
                      setTotpCode('');
                      setTwoFAError('');
                    }}
                    className="w-full text-sm text-surface-400 hover:text-surface-300"
                  >
                    ← Voltar para login
                  </button>
                </motion.div>
              )}
            </CardBody>
          </Card>

          {/* Footer */}
          <p className="text-center text-surface-600 text-sm mt-8">
            © 2024 Hub Business. Todos os direitos reservados.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
